import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import type { ShearAnchorChecksOnElementDTO } from '@api/fastAPI.schemas'
import { useGetShearAnchorChecks } from '@api/shear-anchor-checks/shear-anchor-checks'
import { UseQueryResult } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { buildErrorMessage } from 'src/constants/errors'

/**
 * Shear anchor checks of the active project, keyed per stiffening segment and per wall.
 *
 * Loaded with the other results: the "Schubanker" tab header is tinted red when any shear anchor
 * is over-utilized, so the checks are needed before the tab is opened. The backend caches the
 * dependency node, so only the first read after a change computes anything.
 */
const useShearAnchorChecks = (): UseQueryResult<ShearAnchorChecksOnElementDTO[], AxiosError> => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  return useGetShearAnchorChecks<ShearAnchorChecksOnElementDTO[], AxiosError>(projectId as string, {
    query: {
      enabled: !!projectId,
      // The app default is refetchOnMount: false, which also suppresses the
      // refetch of an *invalidated* query when Results2 remounts (e.g. after a
      // Schubverbinder edit in the system manager) — the tab would keep
      // serving the pre-edit checks. With staleTime Infinity, `true` refetches
      // on mount only when useResultsInvalidation marked the query stale.
      refetchOnMount: true,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Schubanker-Nachweise'), {
          variant: 'error',
        })
      },
    },
  })
}

export default useShearAnchorChecks
